import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../../styled/Button';
import Distance from '../../../../styled/Distance';
import Sticky from '../../../../styled/Sticky';
import useIntersecting from '../../../../hooks/useIntersecting';
import { CartIcon } from '../../../../styled/Icon';
import { Columns, Column } from '../../../../styled/Columns/wrappers';
import useBuyButton from './useBuyButton';
import useContext from '../../../../hooks/useContext';
import Text from '../../../../styled/Text';
import { ProductContext } from '../providers/ProductProvider';
import AlertInactive from '../../../../styled/AlertInactive';
import { DropDownContainer } from '../../../../styled/BuyButton/wrappers';
import Link from '../../../../Router/Link';
import { StyledLink } from '../../../../styled/Link';
import QuantityDropdown from '../../../../components-legacy/QuantityDropdown';
import ProductPrices from '../../../../components-legacy/ProductPrices';
import PriceBadge from '../badges/PriceBadge';
import { FlagType } from '../../../../../@types/product.enum';

const MobileBuyButton = () => {
  const { variantId, closestVariantUrl } = useContext(ProductContext);
  const ref = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const [locked, setLocked] = useState(false);
  useIntersecting(setLocked, ref, true);
  const [quantity, setQuantity] = useState(1);

  const { disabled, message, isActive, showQuantityDropdown, onAddToCart } =
    useBuyButton(variantId);

  const { flags, price } = useContext(ProductContext);

  // Question: Move this to productContext since it is used and init also in Price(ProductPage)?
  const priceDesign = useMemo(
    () => flags.find((i) => i.priceCustomization)?.priceCustomization,
    [flags],
  );

  const hasPriceFlag = useMemo(() => flags?.some((flag) => flag.type === FlagType.price), [flags]);

  // Set height of parent, whole parent must be visible before switching to relative position.
  // Inlude padding-top (16px) and padding-bottom (16px) in height
  useEffect(() => {
    const setHeight = () => {
      if (ref.current && stickyRef.current) {
        ref.current.style.height = `${stickyRef.current.offsetHeight + 16 + 16}px`;
      }
    };

    setHeight();
    window.addEventListener('resize', setHeight);

    return () => window.removeEventListener('resize', setHeight);
  }, []);

  const onSetQuantity = useCallback(
    (quantity) => {
      if (isNaN(quantity)) {
        quantity = 1;
      }
      return setQuantity(quantity);
    },
    [setQuantity],
  );

  return (
    <div className="hidden-md" ref={ref}>
      <Sticky locked={!locked}>
        <div ref={stickyRef}>
          {message && (
            <>
              {!isActive ? (
                <AlertInactive tone="inactive">
                  {message}
                  <Text weight="regular">
                    Se liknande eller produkter ur samma kategori längre ner på sidan.
                  </Text>
                  {closestVariantUrl && (
                    <>
                      <Distance size="1" />
                      <Link
                        href={closestVariantUrl}
                        Component={StyledLink}
                        title="Närmsta möjliga variant">
                        Se närmaste variant här
                      </Link>
                    </>
                  )}
                </AlertInactive>
              ) : (
                <Text>{message}</Text>
              )}
              <Distance />
            </>
          )}
          <Columns className="valign-middle space-2">
            <Column className="grow">
              <PriceBadge />

              {price && (
                <ProductPrices
                  size="large"
                  inline
                  priceDesign={priceDesign}
                  priceObject={{
                    originalPrice: price.originalPrice,
                    sellingPrice: price.price,
                    referencePrice: price.referencePrice,
                    pilPrice: price.pilPrice,
                    componentPrice: price.componentPrice,
                  }}
                  hasPriceFlag={hasPriceFlag}
                />
              )}
            </Column>
            {showQuantityDropdown && (
              <Column>
                <DropDownContainer>
                  <QuantityDropdown
                    disabled={disabled}
                    quantity={quantity}
                    onChange={onSetQuantity}
                  />
                </DropDownContainer>
              </Column>
            )}
            <Column>
              <Button
                disabled={disabled}
                tone="forward"
                rounded
                onClick={() => onAddToCart(quantity)}>
                {!isActive ? (
                  'Ej tillgänglig'
                ) : (
                  <>
                    <CartIcon className="m-r-half" /> Köp{' '}
                  </>
                )}
              </Button>
            </Column>
          </Columns>
        </div>
      </Sticky>
    </div>
  );
};

export default MobileBuyButton;
